// Localização: frontend/src/components/Common/SecondaryButton.jsx
// Componente SecondaryButton - Botão secundário com estilo similar ao PrimaryButton

import React from 'react';

/**
 * Componente SecondaryButton - Botão secundário
 * 
 * @param {React.ReactNode} children - Conteúdo do botão
 * @param {Function} onClick - Função chamada ao clicar
 * @param {string} type - Tipo do botão ('button', 'submit', 'reset')
 * @param {boolean} disabled - Se o botão está desabilitado
 * @param {string} className - Classes CSS adicionais
 * @param {React.ReactNode} icon - Ícone a ser exibido antes do texto
 * @param {string} variant - Variante do botão ('secondary', 'outline', 'danger')
 * @param {string} size - Tamanho do botão ('sm', 'md', 'lg')
 */
const SecondaryButton = ({ 
    children, 
    onClick, 
    type = 'button', 
    disabled = false, 
    className = '', 
    icon,
    variant = 'secondary',
    size = 'md',
    ...restProps
}) => {
    // Variantes de cor
    const variantStyles = {
        secondary: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 shadow-sm hover:shadow-md',
        outline: 'bg-transparent text-gray-700 border-gray-300 hover:bg-gray-50',
        danger: 'bg-white text-red-700 border-red-300 hover:bg-red-50',
    };

    // Tamanhos
    const sizeStyles = {
        sm: 'px-3 py-1.5 text-xs sm:text-sm',
        md: 'px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm',
        lg: 'px-4 py-2.5 sm:px-6 sm:py-3 text-sm sm:text-base',
    };

    // Estilo base
    const baseStyles = `
        flex items-center justify-center gap-2
        rounded-lg
        transition-all duration-200
        font-medium
        border
        outline-none
        focus:outline-none
        focus-visible:outline-none
        focus-visible:ring-0
        focus:ring-0
        select-none
        user-select-none
        disabled:opacity-60 disabled:shadow-none disabled:cursor-not-allowed
        disabled:hover:bg-current
        whitespace-nowrap
    `;

    // Combina estilos
    const buttonStyles = `
        ${baseStyles}
        ${variantStyles[variant] || variantStyles.secondary}
        ${sizeStyles[size] || sizeStyles.md}
        ${className}
    `.trim().replace(/\s+/g, ' ');

    // Handler para prevenir desfoque
    const handleClick = (e) => {
        if (onClick && !disabled) {
            onClick(e);
        }
        if (e.currentTarget) {
            e.currentTarget.blur();
        }
    };

    return (
        <button
            type={type}
            onClick={handleClick}
            disabled={disabled}
            className={buttonStyles}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            {...restProps}
        >
            {icon && <span className="flex-shrink-0">{icon}</span>}
            {children}
        </button>
    );
};

export default SecondaryButton;

